/*
 *  Copyright (C) GridGain Systems. All Rights Reserved.
 *  _________        _____ __________________        _____
 *  __  ____/___________(_)______  /__  ____/______ ____(_)_______
 *  _  / __  __  ___/__  / _  __  / _  / __  _  __ `/__  / __  __ \
 *  / /_/ /  _  /    _  /  / /_/ /  / /_/ /  / /_/ / _  /  _  / / /
 *  \____/   /_/     /_/   \_,__/   \____/   \__,_/  /_/   /_/ /_/
 */

import {
  clusterList,
  currentCluster,
  currentClusterInfo,
  isCurrentClusterinStatus,
  selectRouteParam,
  user,
} from '@app/core/ngrx/app.selectors';
import { AppState } from '@app/core/types';
import { findClusterById } from '@app/domain/clusters';
import { findZoneByName } from '@app/domain/distribution-zone';
import { findTableByFullName } from '@app/domain/tables';
import { SchemaTree } from '@app/sql/types';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { isCluster3x } from '@shared/domain/cluster';
import { ClusterId, ClusterStatus, ProductEdition } from '@shared/types/cluster';
import { entries, groupBy, isEmpty, isNil, memoize, uniq } from 'lodash-es';

// Cluster 3x Topology
export const cluster3xTopologies = createFeatureSelector<AppState['cluster3xTopology']>('cluster3xTopology');

export const cluster3xTopologyByClusterId = (clusterId: string) =>
  createSelector(cluster3xTopologies, (state) => state.get(clusterId));

export const currentCluster3xTopology = createSelector(
  currentCluster,
  isCurrentClusterinStatus(ClusterStatus.DISCONNECTED),
  cluster3xTopologies,
  (clusterId, isCurrentClusterDisconnected, topologies) =>
    clusterId && !isCurrentClusterDisconnected ? topologies.get(clusterId) : undefined,
);
export const currentCluster3xNodes = createSelector(currentCluster3xTopology, (topology) =>
  topology ? topology.nodes : [],
);

export const currentCluster3xHasNodesRunning = createSelector(currentCluster3xNodes, (nodes) =>
  nodes.some((n) => n.status === 'RUNNING'),
);

// Cluster 3x Tables
export const cluster3xTables = createFeatureSelector<AppState['cluster3xTables']>('cluster3xTables');
export const currentCluster3xTables = createSelector(currentCluster, cluster3xTables, (clusterId, tables) =>
  clusterId ? tables.get(clusterId) : [],
);
export const currentCluster3xNotSystemTables = createSelector(
  currentCluster3xTables,
  (tables) => tables?.filter((it) => it.schemaName.toUpperCase() !== 'SYSTEM') ?? [],
);
export const currentCluster3xNotSystemTableNames = createSelector(currentCluster3xNotSystemTables, (tables) =>
  uniq(tables.map((it) => it.tableName)),
);

export const tableNameRouteParam = createSelector(selectRouteParam('tableName'), (value) => value);
export const currentClusterTableByFullName = createSelector(
  currentCluster3xTables,
  tableNameRouteParam,
  (tables, tableName) =>
    isNil(tables) || isEmpty(tables) || !tableName ? undefined : findTableByFullName(tables, tableName),
);

export const currentCluster3xSchemaTree = createSelector(currentCluster3xTables, (tables): SchemaTree[] =>
  entries(groupBy(tables, 'schemaName')).map(([schemaName, tables]) => ({
    name: schemaName,
    tables: tables.map((t) => ({ name: t.tableName, fields: [] })),
  })),
);

// Cluster 3x Distribution zones

export const cluster3xDistributionZones =
  createFeatureSelector<AppState['cluster3xDistributionZones']>('cluster3xDistributionZones');
export const currentCluster3xDistributionZones = createSelector(
  currentCluster,
  cluster3xDistributionZones,
  (clusterId, zones) => (clusterId ? zones.get(clusterId) : []),
);

export const zoneRouteParam = createSelector(selectRouteParam('zoneName'), (value) => value);
export const currentClusterDistributionZoneByName = createSelector(
  currentCluster3xDistributionZones,
  zoneRouteParam,
  (zones, zoneName) => (isNil(zones) || isEmpty(zones) || !zoneName ? undefined : findZoneByName(zones, zoneName)),
);

// Cluster 3x Local Partitions
export const cluster3xLocalPartitionStates = createFeatureSelector<AppState['cluster3xLocalPartitionStates']>(
  'cluster3xLocalPartitionStates',
);
export const currentCluster3xLocalPartitionStates = createSelector(
  currentCluster,
  cluster3xLocalPartitionStates,
  (clusterId, localStates) => (clusterId ? (localStates.get(clusterId) ?? []) : []),
);

// Cluster 3x Configuration
export const cluster3xConfiguration =
  createFeatureSelector<AppState['cluster3xConfiguration']>('cluster3xConfiguration');

export const currentCluster3xConfiguration = createSelector(
  currentCluster,
  cluster3xConfiguration,
  (clusterId, configurations) => (clusterId ? configurations.get(clusterId) : null),
);
// TODO: CC-10176 remove selector usage
export const currentCluster3xHasExporterConfiguration = createSelector(
  currentCluster3xConfiguration,
  (configuration) =>
    !isNil(configuration) &&
    !isNil(configuration.ignite?.metrics?.exporters) &&
    configuration.ignite.metrics.exporters.some(
      ({ exporterName, name }) => exporterName === 'otlp' && name === 'cc_exporter',
    ),
);

// TODO: CC-10176 use instead of currentCluster3xHasExporterConfiguration
export const currentCluster3xHasMetricsConfiguration = createSelector(currentClusterInfo, (cluster) =>
  cluster && isCluster3x(cluster) ? cluster.info.metricsMonitoringEnabled : undefined,
);

export const currentCluster3xHasRunningQueriesConfiguration = createSelector(currentClusterInfo, (cluster) =>
  cluster && isCluster3x(cluster) ? cluster.info.runningQueriesMonitoringEnabled : undefined,
);

// Cluster Connectors
export const clusterConnectorsSelector = createFeatureSelector<AppState['clusterConnectors']>('clusterConnectors');

export const currentClusterConnectorIdSelector = createSelector(currentClusterInfo, (cluster) =>
  cluster && isCluster3x(cluster) ? cluster.info.connectorId : undefined,
);

export const currentClusterConnectorNameSelector = createSelector(currentClusterInfo, (cluster) =>
  cluster && isCluster3x(cluster) ? cluster.info.connectorName : undefined,
);

export const amICurrentClusterOwnerSelector = createSelector(
  currentClusterInfo,
  user,
  (cluster, user) => cluster?.ownerEmail === user?.email,
);

export const isUnknownConnectorSelector = createSelector(
  currentClusterConnectorIdSelector,
  clusterConnectorsSelector,
  amICurrentClusterOwnerSelector,
  (connectorId, connectors, isOwner) =>
    connectorId && isOwner ? !connectors.find((connector) => connector.id === connectorId) : false,
);

export const isClusterAi3Selector = memoize((clusterId: ClusterId | undefined | null) =>
  createSelector(clusterList, (clusters) => {
    const cluster = findClusterById(clusters, clusterId ?? '');
    return cluster?.edition === ProductEdition.AI3;
  }),
);
